import { z } from 'zod';
import { idSchema, isoDateTimeSchema } from './common.js';
import { paginationMetaSchema } from './pagination.js';
import { ledgerDateSchema } from './financial-accounting.js';
export const MAIL_ACCOUNT_TYPES = ['PERSONAL', 'SHARED'];
export const MAIL_RECORD_SHARING = ['PRIVATE', 'MANUAL', 'AUTO_BUSINESS'];
export const MAIL_ACCOUNT_LOCK_NAMESPACE = 714221;
export const MAIL_CONNECTION_TIMEOUT_MS = 20_000;
export const MAIL_CONFIGURATION_TX_TIMEOUT_MS = 30_000;
export const MAIL_ACCOUNT_CREATE_LIMIT_PER_MINUTE = 20;
export const MAIL_SECRET_MAX_LENGTH = 16_384;
export const mailAddressSchema = z.string().trim().email().max(254);
export const mailAccountAliasSchema = z
    .object({
    address: mailAddressSchema,
    displayName: z.string().trim().max(120),
    canSend: z.boolean(),
})
    .strict();
export const mailAccountMemberSchema = z
    .object({
    userId: idSchema,
    isResponsible: z.boolean(),
    canRead: z.boolean(),
    canEdit: z.boolean(),
    canSend: z.boolean(),
    canAdmin: z.boolean(),
    canReadBcc: z.boolean(),
})
    .strict();
export const mailConnectionSchema = z
    .object({
    host: z
        .string()
        .trim()
        .min(1)
        .max(253)
        .regex(/^[a-zA-Z0-9][a-zA-Z0-9.:-]*$/u),
    port: z.number().int().min(1).max(65_535),
    tlsMode: z.enum(['TLS', 'STARTTLS']),
    username: z.string().trim().min(1).max(320),
    authType: z.enum(['PASSWORD', 'OAUTH2']),
    tokenExpiresAt: isoDateTimeSchema.nullish(),
})
    .strict();
const writableConnection = mailConnectionSchema
    .extend({
    secret: z.string().min(1).max(MAIL_SECRET_MAX_LENGTH).optional(),
    clearSecret: z.boolean().optional(),
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.clearSecret && value.secret)
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['secret'],
            message: 'mail.validation.secretConflict',
        });
});
const outgoingSchema = z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('TENANT_SMTP') }).strict(),
    mailConnectionSchema
        .extend({
        mode: z.literal('CUSTOM_SMTP'),
        secret: z.string().min(1).max(MAIL_SECRET_MAX_LENGTH).optional(),
        clearSecret: z.boolean().optional(),
    })
        .strict(),
]);
const mailAccountFieldsSchema = z
    .object({
    displayName: z.string().trim().min(1).max(120),
    primaryAddress: mailAddressSchema,
    type: z.enum(MAIL_ACCOUNT_TYPES),
    aliases: z.array(mailAccountAliasSchema),
    members: z.array(mailAccountMemberSchema).min(1),
    incoming: writableConnection,
    outgoing: outgoingSchema,
    syncFrom: ledgerDateSchema,
    recordSharing: z.enum(MAIL_RECORD_SHARING),
    isActive: z.boolean(),
})
    .strict();
function validateMailAccount(value, ctx) {
    if (!value.members.some((member) => member.isResponsible))
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['members'],
            message: 'mail.validation.responsibleRequired',
        });
    if (value.type === 'PERSONAL' && value.recordSharing === 'AUTO_BUSINESS')
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['recordSharing'],
            message: 'mail.validation.personalSharing',
        });
    const users = new Set();
    for (const [index, member] of value.members.entries()) {
        if (users.has(member.userId))
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['members', index, 'userId'],
                message: 'mail.validation.duplicateMember',
            });
        users.add(member.userId);
    }
    const addresses = new Set([value.primaryAddress.toLowerCase()]);
    for (const [index, alias] of value.aliases.entries()) {
        if (addresses.has(alias.address.toLowerCase()))
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['aliases', index, 'address'],
                message: 'mail.validation.duplicateAddress',
            });
        addresses.add(alias.address.toLowerCase());
    }
    if (value.outgoing.mode === 'CUSTOM_SMTP' && value.outgoing.clearSecret && value.outgoing.secret)
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['outgoing', 'secret'],
            message: 'mail.validation.secretConflict',
        });
}
export const mailAccountInputSchema = mailAccountFieldsSchema.superRefine(validateMailAccount);
export const mailAccountUpdateSchema = mailAccountFieldsSchema
    .extend({ version: z.number().int().positive() })
    .superRefine(validateMailAccount);
export const mailAccountCapabilitiesSchema = z
    .object({
    read: z.boolean(),
    edit: z.boolean(),
    send: z.boolean(),
    admin: z.boolean(),
    bcc: z.boolean(),
})
    .strict();
export const mailEmployeeSchema = z
    .object({ id: idSchema, name: z.string(), email: z.string() })
    .strict();
export const mailAccountSummarySchema = z
    .object({
    id: idSchema,
    version: z.number().int().positive(),
    displayName: z.string(),
    primaryAddress: mailAddressSchema,
    type: z.enum(MAIL_ACCOUNT_TYPES),
    aliases: z.array(mailAccountAliasSchema),
    responsibleEmployees: z.array(mailEmployeeSchema),
    isActive: z.boolean(),
    recordSharing: z.enum(MAIL_RECORD_SHARING),
    syncFrom: ledgerDateSchema,
    timezone: z.string().min(1),
    lastSuccessfulSyncAt: isoDateTimeSchema.nullable(),
    lastAttemptAt: isoDateTimeSchema.nullable(),
    syncStatus: z.enum([
        'NOT_STARTED',
        'QUEUED',
        'RUNNING',
        'PARTIAL',
        'SUCCEEDED',
        'FAILED',
        'PAUSED',
    ]),
    lastErrorCode: z.string().nullable(),
    capabilities: mailAccountCapabilitiesSchema,
    sendAddresses: z.array(z.object({ id: idSchema, address: mailAddressSchema })).default([]),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
})
    .strict();
const readableConnection = mailConnectionSchema.extend({ secretConfigured: z.boolean() }).strict();
export const mailAccountConfigurationSchema = mailAccountSummarySchema
    .extend({
    incoming: readableConnection,
    outgoing: z.discriminatedUnion('mode', [
        z.object({ mode: z.literal('TENANT_SMTP') }).strict(),
        readableConnection.extend({ mode: z.literal('CUSTOM_SMTP') }).strict(),
    ]),
    members: z.array(mailAccountMemberSchema.extend({ employee: mailEmployeeSchema }).strict()),
})
    .strict();
export const mailAccountListQuerySchema = z
    .object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(25),
    q: z.string().trim().max(200).optional(),
    sort: z
        .enum(['displayName:asc', 'displayName:desc', 'updatedAt:desc'])
        .default('displayName:asc'),
    type: z.enum(MAIL_ACCOUNT_TYPES).optional(),
})
    .strict();
export const mailAccountListResponseSchema = z
    .object({ data: z.array(mailAccountSummarySchema), meta: paginationMetaSchema })
    .strict();
export const mailAccountLookupsSchema = z
    .object({ employees: z.array(mailEmployeeSchema) })
    .strict();
//# sourceMappingURL=mail-account.js.map